import React, { useState, useEffect, useContext } from 'react';
import api from '../api';
import { AuthContext } from '../AuthContext';
import { useMultiHOA } from '../MultiHOAContext';

/**
 * HOA and Property Selector Component
 * 
 * Displays dropdowns for:
 * 1. HOA selection (if user belongs to multiple HOAs)
 * 2. Property selection (if user owns multiple properties in current HOA)
 * 
 * Updates localStorage and triggers page refresh when context changes.
 */
const HOAPropertySelector = () => {
  const { user } = useContext(AuthContext);
  const {
    activeHoaId,
    setActiveHoaId,
    activePropertyId,
    setActivePropertyId,
    userHoas,
    setUserHoas,
    userProperties,
    setUserProperties
  } = useMultiHOA();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch user's HOAs on mount
  useEffect(() => {
    const fetchUserHOAs = async () => {
      try {
        setLoading(true);
        const response = await api.get('/api/user-context/hoas');
        const hoas = response.data || [];
        setUserHoas(hoas);

        // If user hasn't selected an HOA yet, set the primary one
        if (!activeHoaId && hoas.length > 0) {
          const primaryHOA = hoas.find(h => h.is_primary) || hoas[0];
          setActiveHoaId(primaryHOA.hoa_id);
        }

        setError(null);
      } catch (err) {
        console.error('Error fetching user HOAs:', err);
        setError('Failed to load HOAs');
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchUserHOAs();
    }
  }, [user]);

  // Fetch user's properties when HOA changes
  useEffect(() => {
    const fetchUserProperties = async () => {
      if (!activeHoaId) return;

      try {
        const response = await api.get(`/api/user-context/properties/${activeHoaId}`);
        const properties = response.data || [];
        setUserProperties(properties);

        // If user hasn't selected a property yet, set the primary voter one
        if (!activePropertyId && properties.length > 0) {
          const primaryProperty = properties.find(p => p.is_primary_voter) || properties[0];
          setActivePropertyId(primaryProperty.property_id);
        }
      } catch (err) {
        console.error('Error fetching user properties:', err);
        // Don't show error - user might have only one property
      }
    };

    if (user && activeHoaId) {
      fetchUserProperties();
    }
  }, [activeHoaId, user]);

  const handleHOAChange = async (e) => {
    const newHoaId = e.target.value;
    setActiveHoaId(newHoaId);
    setActivePropertyId(null); // Clear property when HOA changes
    
    // Validate context change with backend
    try {
      await api.post('/api/user-context/set-context', { hoa_id: newHoaId });
      
      // Reload page to refresh all data with new context
      window.location.reload();
    } catch (err) {
      console.error('Error switching HOA:', err);
      alert('Failed to switch HOA. Please try again.');
    }
  };

  const handlePropertyChange = async (e) => {
    const newPropertyId = e.target.value;
    setActivePropertyId(newPropertyId);
    
    // Validate context change with backend
    try {
      await api.post('/api/user-context/set-context', {
        hoa_id: activeHoaId,
        property_id: newPropertyId
      });
      
      // Reload page to refresh all data with new context
      window.location.reload();
    } catch (err) {
      console.error('Error switching property:', err);
      alert('Failed to switch property. Please try again.');
    }
  };

  if (!user) return null;
  if (loading) return (
    <div className="flex items-center gap-2 text-sm text-gray-600">
      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
      <span>Loading context...</span>
    </div>
  );

  // Don't show selector if user only has one HOA and one property
  if (userHoas.length <= 1 && userProperties.length <= 1) return null;

  const currentHOA = userHoas.find(h => h.hoa_id === activeHoaId);
  const currentProperty = userProperties.find(p => p.property_id === activePropertyId);

  return (
    <div className="flex items-center gap-3">
      {/* HOA Selector - only show if user has multiple HOAs */}
      {userHoas.length > 1 && (
        <div className="flex items-center gap-2">
          <label htmlFor="hoa-selector" className="text-sm font-medium text-gray-700 whitespace-nowrap">
            HOA:
          </label>
          <select
            id="hoa-selector"
            value={activeHoaId || ''}
            onChange={handleHOAChange}
            className="block w-48 px-3 py-1.5 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
          >
            {userHoas.map(hoa => (
              <option key={hoa.hoa_id} value={hoa.hoa_id}>
                {hoa.hoa_name} {hoa.is_primary ? '(Primary)' : ''}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Property Selector - only show if user has multiple properties */}
      {userProperties.length > 1 && (
        <div className="flex items-center gap-2">
          <label htmlFor="property-selector" className="text-sm font-medium text-gray-700 whitespace-nowrap">
            Property:
          </label>
          <select
            id="property-selector"
            value={activePropertyId || ''}
            onChange={handlePropertyChange}
            className="block w-48 px-3 py-1.5 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
          >
            {userProperties.map(property => (
              <option key={property.property_id} value={property.property_id}>
                {property.property_address} {property.is_primary_voter ? '⭐' : ''}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Current Context Display (for single HOA/property users) */}
      {userHoas.length === 1 && userProperties.length === 1 && (
        <div className="text-sm text-gray-600 flex items-center gap-2">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
          </svg>
          <span className="font-medium">{currentHOA?.hoa_name}</span>
          {currentProperty && (
            <>
              <span className="text-gray-400">•</span>
              <span>{currentProperty.property_address}</span>
            </>
          )}
        </div>
      )}

      {error && (
        <div className="text-xs text-red-600">{error}</div>
      )}
    </div>
  );
};

export default HOAPropertySelector;
